import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import NavBar from './NavBar.jsx'
import { useAuth } from '../context/AuthContext.jsx'

function Footer() {
  return (
    <footer className="app-footer">
      <p>Sonologue closed alpha. Everything here is yours to export or delete.</p>
      {/* The class is the only reason this paragraph differs from the one above
          it: its links carry a 32px tap target, and a target taller than the
          line box it sits in reaches into the next line. See `.footer-links`. */}
      <p className="footer-links">
        <Link to="/legal/terms">Terms</Link> · <Link to="/legal/privacy">Privacy</Link> ·{' '}
        Music data from <a href="https://musicbrainz.org" rel="noreferrer noopener" target="_blank">MusicBrainz</a>{' '}
        · art from the{' '}
        <a href="https://coverartarchive.org" rel="noreferrer noopener" target="_blank">
          Cover Art Archive
        </a>
      </p>
    </footer>
  )
}

function Splash({ children }) {
  return (
    <div className="auth-shell">
      <div className="empty-state">{children}</div>
    </div>
  )
}

/** The authenticated shell. Everything inside it has a signed-in user. */
export default function AppLayout() {
  const { status, isAuthenticated, signedOut } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return (
      <Splash>
        <p className="subtle" role="status">
          Loading…
        </p>
      </Splash>
    )
  }

  if (status === 'error') {
    // Distinguished from signed-out on purpose: sending someone to /login
    // because the API is down teaches them their password is wrong.
    return (
      <Splash>
        <h2>Can’t reach Sonologue</h2>
        <p>Sonologue didn’t answer. This is usually temporary — try again in a moment.</p>
        <button type="button" className="btn btn-quiet" onClick={() => window.location.reload()}>
          Try again
        </button>
      </Splash>
    )
  }

  if (!isAuthenticated) {
    // The query and hash too, so a search survives signing in (Q8). Not after
    // a sign-out: that page did not ask anybody to sign in, and the next
    // person to sign in on this device should land on their own feed.
    const from = location.pathname + location.search + location.hash
    return <Navigate to="/login" replace state={signedOut ? undefined : { from }} />
  }

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <NavBar />
      <main className="page" id="main">
        <Outlet />
      </main>
      <Footer />
    </div>
  )
}

/**
 * Open to everybody, signed in or not — the legal pages, the export download
 * and the two account-deletion screens.
 *
 * It cannot be `PublicOnlyLayout`, which sends a signed-in visitor home: the
 * terms have to be readable from the footer of a page somebody is already
 * signed into. And it cannot be `AppLayout`, which sends a signed-out one to
 * the login form: the deletion links are followed by people who no longer have
 * a session, which is the entire point of them.
 */
export function OpenLayout() {
  const { isAuthenticated } = useAuth()
  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      {isAuthenticated ? <NavBar /> : null}
      <main className="page" id="main">
        <Outlet />
      </main>
      <Footer />
    </div>
  )
}

/**
 * The signed-out shell — sends an already-authenticated visitor on: back to
 * the page that sent them to sign in, or home.
 *
 * This redirect is the one that lands after a sign-in, not `Login`'s own
 * `navigate(from)`: the auth update renders first, still at `/login`, and this
 * layout answers it before the page's navigation arrives. Sending everybody to
 * `/` here is how signing in from `/settings` used to end on the feed (Q8).
 * Only a same-site path is honoured; `from` is only ever set by `AppLayout`,
 * but `//host` is a URL, not a path.
 */
export function PublicOnlyLayout() {
  const { status, isAuthenticated } = useAuth()
  const location = useLocation()
  if (status === 'loading') {
    return (
      <Splash>
        <p className="subtle" role="status">
          Loading…
        </p>
      </Splash>
    )
  }
  if (isAuthenticated) {
    const from = location.state?.from
    const back = typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
    return <Navigate to={back ? from : '/'} replace />
  }
  return <Outlet />
}
